import { useState } from "react";
import { API_BASE } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { FileText, Image as ImageIcon, Video, Download, Eye } from "lucide-react";

const KIND_ICON = (ct) => {
  if (!ct) return FileText;
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Video;
  return FileText;
};

export function MediaThumb({ media }) {
  const [open, setOpen] = useState(false);
  const url = `${API_BASE}/files/${media.id}`;
  const Icon = KIND_ICON(media.content_type);
  const isImg = media.content_type?.startsWith("image/");
  const isVid = media.content_type?.startsWith("video/");
  const isPdf = media.content_type === "application/pdf";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`media-thumb-${media.id}`}
          className="group aspect-video w-full rounded-md bg-slate-100 border border-slate-200 overflow-hidden relative hover:border-slate-400 transition-all"
        >
          {isImg ? (
            <img
              src={url}
              alt={media.original_filename}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 gap-1.5 p-2">
              <Icon className="w-8 h-8" strokeWidth={1.5} />
              <div className="text-[10px] font-mono-cms text-slate-500 uppercase">
                {media.original_filename?.split(".").pop() || "file"}
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white text-xs font-semibold">
            <Eye className="w-4 h-4" /> Preview
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-base">
            {media.title || media.original_filename}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Media preview and download for {media.original_filename}
          </DialogDescription>
        </DialogHeader>
        <div className="bg-slate-100 rounded-md overflow-hidden">
          {isImg && (
            <img src={url} alt={media.original_filename} className="w-full" />
          )}
          {isVid && (
            <video controls src={url} className="w-full max-h-[70vh]" />
          )}
          {isPdf && (
            <iframe
              src={url}
              title={media.original_filename}
              className="w-full h-[70vh]"
            />
          )}
          {!isImg && !isVid && !isPdf && (
            <div className="p-10 text-center text-slate-500 text-sm">
              Preview not supported. Please download to view.
            </div>
          )}
        </div>
        {media.description && (
          <p className="text-sm text-slate-600">{media.description}</p>
        )}
        <DialogFooter>
          <a
            href={url}
            download={media.original_filename}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700"
          >
            <Download className="w-4 h-4" /> Download
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MediaThumb;
